import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Button, SimpleGrid, Skeleton, Image, Alert, AlertIcon } from '@chakra-ui/react';
import { Add } from '@mui/icons-material';

import AdminLayout from '../../components/admin/AdminLayout';
import { AdminCard as Card, Delta, KpiTile as Stat, Segmented, Meter } from '../../components/admin/ui';
import RevenueChart from '../../components/admin/RevenueChart';
import StatusBadge, { ORDER_STATUS } from '../../components/admin/StatusBadge';
import { getAdminStats } from '../../services/AdminServices';
import { useUserContext } from '../../contexts/UserContext';
import { inr, shortId, formatDateTime } from '../../utils/format';

const Dashboard = () => {

    const navigate = useNavigate();
    const { currentUser } = useUserContext();
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setStats(null);
        setError('');
        getAdminStats(currentUser, days)
            .then(setStats)
            .catch(e => setError(e.response?.data?.error || 'Could not load stats.'));
    }, [currentUser, days]);

    const openOrders = (status) => navigate('/admin/orders', { state: { status } });
    const statusTotal = stats ? Object.values(stats.statusCounts).reduce((a, b) => a + b, 0) : 0;
    const topMax = stats?.topProducts?.[0]?.quantity || 1;

    return (
        <AdminLayout
            title='Dashboard'
            subtitle={`Last ${days} days · India time`}
            actions={<Button variant='ink' height='40px' px='18px' fontSize='15px' leftIcon={<Add fontSize='small' />} onClick={() => navigate('/admin/products', { state: { create: true } })} >Add product</Button>}
        >
            <Box mb='16px' >
                <Segmented label='Date range' value={days} onChange={setDays} options={[7, 30, 90].map(d => ({ value: d, label: `${d} days` }))} />
            </Box>

            {error && <Alert status='error' borderRadius='8px' mb='16px' ><AlertIcon />{error}</Alert>}

            {!stats && !error ?
                <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3} >
                    {Array(6).fill(null).map((_, i) => <Skeleton key={i} height='110px' borderRadius='12px' gridColumn={i === 0 ? { base: 'span 2', lg: 'span 4' } : undefined} />)}
                </SimpleGrid>
                : stats && <>
                    {/* Hero figure: the one number this view leads with */}
                    <Box mb='12px' >
                        <Stat hero label='Revenue' value={inr(stats.revenue)} sub={<><Delta current={stats.revenue} previous={stats.previousRevenue} /><Text mt='6px' >Excludes cancelled orders.</Text></>} />
                    </Box>

                    {stats.estimated &&
                        <Alert status='info' variant='left-accent' borderRadius='8px' mb='12px' fontSize={13} py={2} >
                            <AlertIcon />Some older orders didn't record their price at checkout, so their totals are estimated from current prices.
                        </Alert>}

                    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing='12px' mb='12px' >
                        <Stat label='Orders' value={stats.orders} sub={<Delta current={stats.orders} previous={stats.previousOrders} />} />
                        <Stat label='Avg. order value' value={inr(stats.averageOrderValue)} />
                        <Stat label='Buyers' value={stats.buyers} sub={`${stats.customers} registered customers`} />
                        <Stat label='To ship' value={stats.toShip} sub={stats.toShip ? 'Tap to view →' : 'All caught up'} onClick={() => openOrders('preparing')} />
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing='12px' >
                        <Card title={`Revenue per ${stats.bucket}`} gridColumn={{ lg: 'span 2' }} >
                            <RevenueChart series={stats.series} bucket={stats.bucket} />
                        </Card>

                        <Card title='Orders by status' action='All orders' onAction={() => openOrders('all')} >
                            {Object.entries(stats.statusCounts).map(([key, count]) => (
                                <Box key={key} as='button' width='100%' textAlign='left' py={2} onClick={() => openOrders(key)} >
                                    <Box display='flex' justifyContent='space-between' alignItems='center' mb={1} >
                                        <StatusBadge status={key} size='sm' />
                                        <Text fontWeight={700} fontSize={14} >{count}</Text>
                                    </Box>
                                    {/* Meter: same-ramp track, fill = share of all orders */}
                                    <Meter value={statusTotal ? (count / statusTotal) * 100 : 0} color={ORDER_STATUS[key].color} />
                                </Box>
                            ))}
                        </Card>

                        <Card title='Top products' gridColumn={{ lg: 'span 2' }} >
                            {stats.topProducts.length ? stats.topProducts.map((p, i) => (
                                <Box key={p.id} display='flex' alignItems='center' gap={3} py={2} borderTop={i ? '1px solid' : 'none'} borderColor='raano.border' >
                                    <Text width='18px' fontSize={13} fontWeight={700} color='blackAlpha.500' >{i + 1}</Text>
                                    <Image src={p.imageUrl} alt='' boxSize='44px' objectFit='cover' objectPosition='top' borderRadius='6px' bg='raano.imageBg' fallback={<Box boxSize='44px' borderRadius='6px' bg='raano.imageBg' />} />
                                    <Box flex={1} minWidth={0} >
                                        <Text fontSize={14} fontWeight={600} noOfLines={1} >{p.name}</Text>
                                        <Box mt='6px' ><Meter value={(p.quantity / topMax) * 100} /></Box>
                                    </Box>
                                    <Box textAlign='right' flexShrink={0} >
                                        <Text fontSize='16px' fontWeight={700} sx={{ fontStretch: '75%' }} >{p.quantity} sold</Text>
                                        <Text fontSize={12} color='blackAlpha.600' >{inr(p.revenue)}</Text>
                                    </Box>
                                </Box>
                            )) : <Text fontSize={14} color='blackAlpha.600' >No sales in this period yet.</Text>}
                        </Card>

                        <Card title='Recent orders' action='View' onAction={() => openOrders('all')} >
                            {stats.recentOrders.length ? stats.recentOrders.map((o, i) => (
                                <Box key={o._id} as='button' width='100%' textAlign='left' display='flex' justifyContent='space-between' alignItems='center' gap={2} py={2} borderTop={i ? '1px solid' : 'none'} borderColor='raano.border' onClick={() => navigate('/admin/orders', { state: { status: 'all', focus: o._id } })} >
                                    <Box minWidth={0} >
                                        <Text fontSize={14} fontWeight={600} noOfLines={1} >{o.buyer} <Text as='span' color='blackAlpha.500' fontWeight={500} >{shortId(o._id)}</Text></Text>
                                        <Text fontSize={12} color='blackAlpha.600' >{formatDateTime(o.orderDate)} · {o.itemCount} {o.itemCount === 1 ? 'item' : 'items'}</Text>
                                    </Box>
                                    <Box textAlign='right' flexShrink={0} >
                                        <Text fontSize={14} fontWeight={700} >{inr(o.total)}</Text>
                                        <StatusBadge status={o.status} size='sm' />
                                    </Box>
                                </Box>
                            )) : <Text fontSize={14} color='blackAlpha.600' >No orders yet.</Text>}
                        </Card>
                    </SimpleGrid>
                </>}
        </AdminLayout>
    )
}

export default Dashboard;
